import React, { useEffect, useState, useMemo } from 'react';
import { FaCheck } from 'react-icons/fa';
import './AssignStaffStep.css';

// Reuses the requirements payload returned by the quote-gate endpoint to build
// a list of every contractor staff member who counts toward at least one
// requirement, with checkboxes so the contractor picks who they're assigning.
//
// Reports back via onChange(userIds: number[]) and onValidityChange(bool).
const AssignStaffStep = ({ requirements = [], onChange, onValidityChange }) => {
  const [picked, setPicked] = useState(new Set());

  // Flatten every distinct eligible engineer across all requirements.
  const allStaff = useMemo(() => {
    const seen = new Map();
    for (const r of requirements) {
      for (const s of (r.eligible_staff || [])) {
        if (!seen.has(s.user_id)) {
          seen.set(s.user_id, { ...s, qualifies_for: [r.name] });
        } else {
          seen.get(s.user_id).qualifies_for.push(r.name);
        }
      }
    }
    return Array.from(seen.values());
  }, [requirements]);

  // For validity, count how many picked users cover each requirement.
  const validity = useMemo(() => {
    const result = requirements.map(r => {
      const eligibleIds = new Set((r.eligible_staff || []).map(s => s.user_id));
      const covering = Array.from(picked).filter(id => eligibleIds.has(id)).length;
      return { name: r.name, qualification_id: r.qualification_id, min_count: r.min_count, covering };
    });
    const ok = result.every(r => r.covering >= r.min_count);
    return { rows: result, ok };
  }, [picked, requirements]);

  useEffect(() => {
    if (typeof onChange === 'function') onChange(Array.from(picked));
    if (typeof onValidityChange === 'function') onValidityChange(validity.ok);
  }, [picked, onChange, onValidityChange, validity.ok]);

  const toggle = (uid) => {
    setPicked(prev => {
      const next = new Set(prev);
      if (next.has(uid)) next.delete(uid); else next.add(uid);
      return next;
    });
  };

  if (!requirements || requirements.length === 0) return null;
  if (allStaff.length === 0) return null;

  return (
    <div className="cs-ass">
      <div className="cs-ass-title">Assign staff to this quote</div>
      <p className="cs-ass-help">
        Pick the engineers from your team you're putting on this job. They must cover every requirement.
      </p>

      <ul className="cs-ass-staff">
        {allStaff.map(s => {
          const isPicked = picked.has(s.user_id);
          return (
            <li key={s.user_id} className={`cs-ass-row ${isPicked ? 'picked' : ''}`}>
              <label>
                <input type="checkbox" checked={isPicked} onChange={() => toggle(s.user_id)} />
                <span className="cs-ass-name">{s.name}</span>
                <span className="cs-ass-quals">
                  Qualifies for: {s.qualifies_for.join(', ')}
                </span>
              </label>
            </li>
          );
        })}
      </ul>

      <div className={`cs-ass-status ${validity.ok ? 'ok' : 'todo'}`}>
        {validity.ok ? (
          <>
            <FaCheck /> Coverage complete — every requirement is met.
          </>
        ) : (
          <>
            Still needed:
            <ul>
              {validity.rows.filter(r => r.covering < r.min_count).map(r => (
                <li key={r.qualification_id}>
                  {r.name}: need {r.min_count - r.covering} more
                </li>
              ))}
            </ul>
          </>
        )}
      </div>
    </div>
  );
};

export default AssignStaffStep;
